import { ExportProgress, exportToExcel } from '../utils/excel-export.utils';
import { getItemFields, getSubItemFields } from '../utils/excel-hooks.utils';
import { useCallback, useMemo, useRef, useState } from 'react';

import APIService from '../../../services/api.service';
import { BackendItem } from '../../items/types/backend-item.types';
import { NON_FILTER_FIELDS } from '../../items/state/filtering.state';
import { TableRowData } from '../types/excel.types';
import { generateExportFilename } from '../utils/excel-filename.utils';
import omit from 'lodash/fp/omit';
import { useHiddenColumnState } from '../../items/state/column-hiding.state';
import { useTableDataState } from '../../items/state/table-data.state';

export const MEMORY_LIMIT = {
   MAX_ITEMS: 100000,
   MAX_ROWS: 500000,
};

const BATCH_SIZE = 10000;

export interface ExcelExportOptions {
   getData?: () => TableRowData[] | Promise<TableRowData[]>;
   filename?: string;
   includeTimestamp?: boolean;
   onSuccess?: () => void;
   onError?: (error: Error) => void;
   onProgress?: (progress: ExportProgress) => void;
   onFileProgress?: (current: number, total: number) => void;
   getSignal?: () => AbortSignal | undefined;
}

export const useExcelExport = (options?: ExcelExportOptions) => {
   const [isExporting, setIsExporting] = useState(false);
   const optionsRef = useRef(options);
   optionsRef.current = options;

   const exportData = useCallback(async (dataOrGetter?: TableRowData[] | (() => TableRowData[] | Promise<TableRowData[]>)) => {
      setIsExporting(true);
      const opts = optionsRef.current;

      try {
         let data: TableRowData[];

         if (typeof dataOrGetter === 'function') {
            data = await dataOrGetter();
         } else if (dataOrGetter) {
            data = dataOrGetter;
         } else if (opts?.getData) {
            data = await Promise.resolve(opts.getData());
         } else {
            throw new Error('No data source provided');
         }

         if (!data?.length) {
            throw new Error('No data to export');
         }

         const signal = opts?.getSignal?.();

         await exportToExcel(data, opts?.filename, opts?.includeTimestamp ?? true, opts?.onProgress, opts?.onFileProgress, signal);
         opts?.onSuccess?.();
      } catch (err) {
         const error = err instanceof Error ? err : new Error('Export failed');
         opts?.onError?.(error);
         throw error;
      } finally {
         setIsExporting(false);
      }
   }, []);

   return { exportData, isExporting };
};

export interface ExportDataCallbacks {
   onBatchProgress?: (batchNumber: number, itemsFetched: number) => void;
   onFetchComplete?: (totalFetched: number) => void;
}

export const useExportData = (callbacks?: ExportDataCallbacks) => {
   const query = useTableDataState((state) => state.query);
   const inactivatedRowsHidden = useTableDataState((state) => state.query.inactivatedRowsHidden || false);
   const [isLoadingAll, setIsLoadingAll] = useState(false);
   const callbacksRef = useRef(callbacks);
   callbacksRef.current = callbacks;

   const getVisibleExportColumns = useHiddenColumnState((state) => state.getVisibleExportColumns);

   const transformItemsToExportData = useCallback(
      (items: BackendItem[], hideInactive: boolean): TableRowData[] => {
         if (!items?.length) return [];

         const visibleColumns = getVisibleExportColumns();
         const currentVisibleColumns = hideInactive ? visibleColumns.filter((col) => col !== 'active') : visibleColumns;

         const exportData: TableRowData[] = [];

         for (const item of items) {
            const itemFields = getItemFields(item);
            let isFirstRowForItem = true;

            if (!item.materialSets?.length) {
               exportData.push({
                  ...itemFields,
                  _visibleColumns: currentVisibleColumns,
                  _isFirstRowForItem: true,
               });
               continue;
            }

            for (const matSet of item.materialSets) {
               if (!matSet.supplierMaterials?.length) {
                  exportData.push({
                     ...itemFields,
                     _visibleColumns: currentVisibleColumns,
                     _isFirstRowForItem: isFirstRowForItem,
                  });
                  isFirstRowForItem = false;
                  continue;
               }

               for (const subItem of matSet.supplierMaterials) {
                  if (hideInactive && subItem.active === false) {
                     continue;
                  }

                  exportData.push({
                     ...itemFields,
                     ...getSubItemFields(subItem),
                     matSetName: matSet.name,
                     _visibleColumns: currentVisibleColumns,
                     _isFirstRowForItem: isFirstRowForItem,
                  });

                  isFirstRowForItem = false;
               }
            }
         }

         return exportData;
      },
      [getVisibleExportColumns]
   );

   const getAllFilteredExportData = useCallback(
      async (signal?: AbortSignal): Promise<TableRowData[]> => {
         setIsLoadingAll(true);

         try {
            const allItems: BackendItem[] = [];
            let offset = 0;
            let hasMore = true;
            let batchNumber = 1;

            while (hasMore) {
               if (signal?.aborted) {
                  throw new Error('Export cancelled');
               }

               const response = await APIService.getItemsForExport({
                  ...query,
                  pageSize: BATCH_SIZE,
                  offset: offset,
               });

               if (!response?.items || !Array.isArray(response.items)) {
                  if (batchNumber === 1) {
                     throw new Error('Invalid response from server. Please try again.');
                  }
                  break;
               }

               if (response.items.length === 0 && batchNumber === 1) {
                  throw new Error('No items match your current filters.');
               }

               allItems.push(...response.items);
               offset += response.items.length;
               hasMore = response.items.length === BATCH_SIZE;

               if (allItems.length > MEMORY_LIMIT.MAX_ITEMS) {
                  throw new Error(
                     `Export limit reached: ${allItems.length.toLocaleString()} items fetched, ` +
                     `exceeding the maximum of ${MEMORY_LIMIT.MAX_ITEMS.toLocaleString()}. ` +
                     `Please apply additional filters to reduce the dataset size.`
                  );
               }

               callbacksRef.current?.onBatchProgress?.(batchNumber, allItems.length);
               batchNumber++;
            }

            callbacksRef.current?.onFetchComplete?.(allItems.length);

            if (allItems.length === 0) {
               throw new Error('No items fetched. Please check your filters.');
            }

            const exportData = transformItemsToExportData(allItems, inactivatedRowsHidden);

            if (exportData.length === 0) {
               throw new Error('No exportable data generated. All items may be filtered out.');
            }

            if (exportData.length > MEMORY_LIMIT.MAX_ROWS) {
               throw new Error(
                  `Export would generate ${exportData.length.toLocaleString()} rows, ` +
                  `exceeding the limit of ${MEMORY_LIMIT.MAX_ROWS.toLocaleString()}. ` +
                  `Please apply additional filters.`
               );
            }

            return exportData;
         } catch (error) {
            throw error instanceof Error ? error : new Error('Failed to fetch all filtered items. Please try again.');
         } finally {
            setIsLoadingAll(false);
         }
      },
      [query, transformItemsToExportData, inactivatedRowsHidden]
   );

   const exportFilename = useMemo(() => {
      const filters = omit(NON_FILTER_FIELDS)(query);
      return generateExportFilename('material_export', filters, true);
   }, [query]);

   return {
      getAllFilteredExportData,
      isLoadingAll,
      exportFilename,
   };
};
